import { checkNestedFromValidation } from './Util'

export const validationMessages = {
  regex: 'Invalid format',
  email: 'Invalid email address',
  required: 'This field is required',
  pastDate: 'Date must be in the past',
  futureDate: 'Date must be in the future',
  pastDateWithSameDay: 'Date must be in the past or today',
  futureDateWithSameDay: 'Date must be in the future or today',
  alphaNumeric: 'Only alphanumeric characters allowed.',
  alphaNumericWithSpace: 'Only alphanumeric characters and spaces allowed.',
  uppercaseAlphabaticWithUnderscore: 'Only uppercase alphabatic characters with _ allowed.',
  minValueCompareError: (dependentKey?: string) => `Must be later than ${dependentKey}`,
  maxValueCompareError: (dependentKey?: string) => `Must be earlier than ${dependentKey}`,
  minValue: (minValue: string | number) => `Minimum value is ${minValue}`,
  maxValue: (maxValue: string | number) => `Maximum value is ${maxValue}`,
  minLength: (minLength: string | number) => `Minimum length is ${minLength} characters`,
  maxLength: (maxLength: string | number) => `Maximum length is ${maxLength} characters`,
  minDate: (minDate: string | number | Date) =>
    `Date must be after ${new Date(minDate).toLocaleDateString()}`,
  maxDate: (maxDate: string | number | Date) =>
    `Date must be before ${new Date(maxDate).toLocaleDateString()}`,
  wholeNumber: 'Only whole number values allowed',
}

interface FieldConfig {
  id: number
  label: string
  fieldName: string
  fieldType: string
  dataType: string
  placeholder: string
  isValid: boolean
  errorMessage: string
  value: any
  dependencyIndex?: number
  validations: Record<string, any>
  isHidden?: boolean
}

export const validationFunctions: Record<string, any> = {
  required: (value: any) => {
    if (Array.isArray(value)) {
      return value.length > 0 ? '' : validationMessages.required
    }
    return value !== undefined && value !== null && value !== '' ? '' : validationMessages.required
  },
  wholeNumber: (value: number) => {
    const isValue = value === undefined || value === null ? 0 : value
    return Number(isValue) >= 0 ? '' : validationMessages.wholeNumber
  },
  minLength: (value: string, minLength: number) =>
    value.length >= minLength ? '' : validationMessages.minLength(minLength),
  maxLength: (value: string, maxLength: number) =>
    value.length <= maxLength ? '' : validationMessages.maxLength(maxLength),
  minValue: (value: number, minValue: number) =>
    value >= minValue ? '' : validationMessages.minValue(minValue),
  maxValue: (value: number, maxValue: number) =>
    value <= maxValue ? '' : validationMessages.maxValue(maxValue),
  minDate: (value: string, minDate: string | Date) =>
    new Date(value) >= new Date(minDate) ? '' : validationMessages.minDate(minDate),
  minCompareDate: (
    value: string,
    minDate: string | Date,
    dependentDate: string | Date,
    dependentKey?: string,
  ) => {
    if (value && dependentDate)
      return new Date(value) >= new Date(dependentDate)
        ? ''
        : validationMessages.minValueCompareError(dependentKey)
  },
  maxDate: (value: string, maxDate: string | Date) =>
    new Date(value) <= new Date(maxDate) ? '' : validationMessages.maxDate(maxDate),
  maxCompareDate: (
    value: string,
    maxDate: string | Date,
    dependentDate: string | Date,
    dependentKey?: string,
  ) => {
    if (value && dependentDate)
      return new Date(value) <= new Date(dependentDate)
        ? ''
        : validationMessages.maxValueCompareError(dependentKey)
  },
  email: (value: string) => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/ // NOSONAR
    return emailPattern.test(value) ? '' : validationMessages.email
  },
  alphaNumeric: (value: string) => {
    const alphaNumericPattern = '^[a-zA-Z0-9]+$'
    const regex = new RegExp(alphaNumericPattern)
    return regex.test(value) ? '' : validationMessages.alphaNumeric
  },
  uppercaseAlphabaticWithUnderscore: (value: string) => {
    const uppercasePattern = '^[A-Z_]+$'
    const regex = new RegExp(uppercasePattern)
    return regex.test(value) ? '' : validationMessages.uppercaseAlphabaticWithUnderscore
  },
  alphaNumericWithSpace: (value: string) => {
    const alphaNumericPattern = '^[a-zA-Z0-9 ]*$'
    const regex = new RegExp(alphaNumericPattern)
    return regex.test(value) ? '' : validationMessages.alphaNumericWithSpace
  },
  alphaNumericWithSpaceAndUnderscore: (value: string) => {
    const alphaNumericPattern = '^[a-zA-Z0-9 _]*$'
    const regex = new RegExp(alphaNumericPattern)
    return regex.test(value) ? '' : validationMessages.alphaNumericWithSpace
  },
  regex: (value: string, pattern: string) => {
    const regex = new RegExp(pattern)
    return regex.test(value) ? '' : validationMessages.regex
  },
  pastDate: (value: string) => {
    return new Date(value) < new Date() ? '' : validationMessages.pastDate
  },
  futureDate: (value: string) => {
    if (value) return new Date(value) > new Date() ? '' : validationMessages.futureDate
  },
  pastDateWithSameDay: (value: string) => {
    const today = new Date()
    const dateValue = new Date(value)
    today.setHours(0, 0, 0, 0) // Reset time part
    dateValue.setHours(0, 0, 0, 0) // Reset time part
    return dateValue <= today ? '' : validationMessages.pastDateWithSameDay
  },
  futureDateWithSameDay: (value: string) => {
    const today = new Date()
    const dateValue = new Date(value)
    today.setHours(0, 0, 0, 0) // Reset time part
    dateValue.setHours(0, 0, 0, 0) // Reset time part
    return dateValue >= today ? '' : validationMessages.futureDateWithSameDay
  },
}

export const validateField = (
  fieldConfig: FieldConfig,
  value: string | number | Date | null | undefined | Array<any>,
  trim = false,
  allFields?: any,
): {
  value: string | number | Date | null | undefined | Array<any>
  isValid: boolean
  errorMessage: string
} => {
  let error = ''
  let trimmedValue = value
  // Trim the value if it is a string and trim is true
  if (typeof value === 'string' && trim) {
    trimmedValue = value.trim()
  }
  let dependentValue
  let dependentKey
  if (fieldConfig.dependencyIndex) {
    dependentValue = allFields[fieldConfig.dependencyIndex]?.value
    dependentKey = allFields[fieldConfig.dependencyIndex]?.label
  }
  if (fieldConfig.validations && !fieldConfig.isHidden) {
    for (const [key, validationValue] of Object.entries(fieldConfig.validations)) {
      const validationFunction = validationFunctions[key]
      if (validationFunction) {
        const validationError = validationFunction(
          trimmedValue,
          validationValue,
          dependentValue,
          dependentKey,
        )
        if (validationError) {
          error = validationError
          break
        }
      }
    }
  }

  return {
    value: trimmedValue,
    isValid: !error,
    errorMessage: error,
  }
}

export const validateForm = (
  formConfig: FieldConfig[],
  trim = false,
): { formConfig: FieldConfig[]; isValid: boolean } => {
  const validatedFields = formConfig.map((field: any) => {
    if (field.fieldType === 'JSON1') {
      const fields = field.fields.map((ele: any) => {
        return ele.map((item: any) => {
          const { value, isValid, errorMessage } = validateField(item, item.value, true, ele)
          return { ...item, isValid, errorMessage, value }
        })
      })
      return { ...field, fields: fields }
    }
    const { value, isValid, errorMessage } = validateField(field, field.value, trim, formConfig)
    return { ...field, isValid, errorMessage, value }
  })
  // const isFormValid = validatedFields.every((field) => field.isValid)
  const isFormValid: any = checkNestedFromValidation(validatedFields)

  return {
    formConfig: validatedFields,
    isValid: isFormValid,
  }
}

export const getValuesFromFormConfig = (formConfig: any[], initialData: any = {}) => {
  return formConfig.reduce((acc: any, field: any) => {
    if (field.fieldType === 'SELECT' && !field.isMultiple && field.value) {
      acc[field?.fieldName] = field?.valueKey ? field.value[field.valueKey] : field.value.key
    } else if (field.fieldType === 'SELECT' && field.isMultiple && field.value) {
      acc[field?.fieldName] = field.value.map((ele: any) => (field?.saveKey ? ele?.key : ele.value))
    } else if (field.fieldType === 'JSON1') {
      const value: any = []
      field.fields.forEach((ele: any) => {
        value.push(getValuesFromFormConfig(ele))
      })
      acc[field?.fieldName] = value
    } else {
      acc[field?.fieldName] = field.value
    }
    return acc
  }, initialData)
}

export const validateFieldValuesUsingOperator = (fieldValue: any, value: any, operator: string) => {
  if (operator === '===') {
    return fieldValue === value
  } else if (operator === '!==') {
    return fieldValue !== value
  } else if (operator === '>=') {
    return fieldValue >= value
  } else if (operator === '<=') {
    return fieldValue <= value
  } else if (operator === '>') {
    return fieldValue > value
  } else if (operator === '<') {
    return fieldValue < value
  }
  return false
}

interface Field {
  visibilityDependentCondition?: {
    fieldIndex: number
    fieldValue: any
    operator: string
  }
  clearDependentCondition?: {
    fieldIndex: number
    fieldValue: any
  }[]
  [key: string]: any
}
export const VALID_ENTITLEMENT_CHANGE_TYPE: any = {
  'SVOD->Free': true,
  'Free->SVOD': true,
}
export const handleVisibilityAndDependentCondition = (
  field: Field,
  value: any,
  partnersFormFieldsCopy: any[],
): void => {
  const handleVisibilityCondition = (currentField: Field, currentValue: any, formFields: any[]) => {
    if (currentField?.visibilityDependentCondition) {
      const { fieldIndex, fieldValue, operator } = currentField.visibilityDependentCondition

      const isVisible = validateFieldValuesUsingOperator(fieldValue, currentValue, operator)
      formFields[fieldIndex] = {
        ...formFields[fieldIndex],
        isHidden: !isVisible,
        value: !isVisible ? null : formFields[fieldIndex].value,
      }
    }
  }

  if (field?.clearDependentCondition?.length) {
    field.clearDependentCondition.forEach((item) => {
      const { fieldIndex, fieldValue } = item
      const currentValue = partnersFormFieldsCopy[item.fieldIndex]?.value?.value
      const newValue = value?.value
      const isClearRequired = VALID_ENTITLEMENT_CHANGE_TYPE[`${currentValue}->${newValue}`]
      partnersFormFieldsCopy[fieldIndex] = {
        ...partnersFormFieldsCopy[fieldIndex],
        ...(!isClearRequired && {
          value: fieldValue,
        }),
        isValid: true,
        errorMessage: '',
      }

      handleVisibilityCondition(
        partnersFormFieldsCopy[fieldIndex],
        partnersFormFieldsCopy[fieldIndex].value,
        partnersFormFieldsCopy,
      )
    })
  }

  handleVisibilityCondition(field, value, partnersFormFieldsCopy)
}
